import React, {ChangeEventHandler} from 'react';
import {FormText, Col} from "react-bootstrap";
import {concepts, User, GroupRole} from "fearless-common";
import {useField} from "formik";
import Select from "../forms/Select";
import {getGroupRoleOptions, getRoleLabel} from "../../libs/backendEnums";
import UserCard from "../layout/UserCard";
import isAdmin from "../../utils/isAdmin";
import useAuth from "../hooks/useAuth";
import getViaRuleText from "../subscriptions/getViaRuleText";
import RemoveOrGrantSeatModal from "../billing/RemoveOrGrantSeatModal";

const margin = 'me-2';

function SubscribedLabelForAdmins({
  user, groupId, readOnly, reload
}: { user: User, groupId: number, readOnly: boolean, reload: () => void; }) {
  const photo51 = user?.subscription_per_product.photo51;

  if (photo51?.group?.id == groupId) {
    return (
      <>
        <span className={margin}>Yes</span>
        {!readOnly && <RemoveOrGrantSeatModal user={user} groupId={groupId} onSuccess={reload} hasSeatCurrently />}
      </>
    );
  }
  if (photo51?.via === 'group') {
    return <span>Via another account</span>;
  }

  if (photo51?.via === 'individual') {
    return <span>Individually</span>;
  }

  if (photo51?.via === 'rule' && photo51.rule) {
    return <span>{getViaRuleText(photo51.rule)}</span>;
  }
  return <>
    <span className={margin}>No</span>
    {!readOnly && <RemoveOrGrantSeatModal user={user} groupId={groupId} onSuccess={reload} hasSeatCurrently={false} />}
  </>
}

function getSubscribedLabelForMembers(user: User, groupId: number) {
  const photo51 = user?.subscription_per_product.photo51;
  if (photo51?.group?.id == groupId) {
    return <span>Yes, via this {concepts.Group.toLowerCase()}</span>
  }
  if (photo51?.via === 'group') {
    return <span>Yes, via another {concepts.Group.toLowerCase()}</span>;
  }

  if (photo51?.via === 'individual') {
    return <span>Individually</span>;
  }

  if (photo51?.via === 'rule' && photo51.rule) {
    return <span>{getViaRuleText(photo51.rule)}</span>;
  }
  return <>-</>
}

/**
 * Row in a group user table.
 */
export default function UserRow({ item: user, name, onChange, groupId, groupRole, readOnly, reload }: {
    item: User;
    name: string;
    groupId: number;
    readOnly : boolean;
    /** Logged-in user group role. Group admins can see more options for editing. */
    groupRole: GroupRole | null;
    reload: () => void;
    onChange: ChangeEventHandler<HTMLSelectElement>;
}) {
    const { user: loggedInUser } = useAuth();
    const fieldName = `${name}.pivot.role`;
    const [{ value: role }] = useField(fieldName);

    return (
        <>
            <Col sm={3}>
                <UserCard user={user} />
            </Col>
            <Col sm={3}>
              <FormText>
                {(groupRole === GroupRole.Manager || isAdmin(loggedInUser)) ? (
                    <SubscribedLabelForAdmins
                      user={user}
                      groupId={groupId}
                      readOnly={readOnly}
                      reload={reload}
                    />
                ) : (
                  getSubscribedLabelForMembers(user, groupId)
                )}
              </FormText>
            </Col>
            <Col sm={3} className={readOnly ? undefined : "px-0"}>
                {readOnly ? (
                    <FormText>{role ? getRoleLabel(role) : null}</FormText>
                ) : (
                    <Select
                        name={fieldName}
                        label={undefined}
                        onChange={onChange}
                        options={getGroupRoleOptions()}
                    />
                )}
            </Col>
        </>
    );
}
